import { Injectable } from '@angular/core';

import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { OddsOperations } from '@frontend/sports/odds/feature';
import { PlaceBetStatus, PlacementErrorType } from '@frontend/sports/types/betslip';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { flatten } from 'lodash-es';
import { filter, map, tap } from 'rxjs';

import { selectIsLinearBetslip } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { isBaseBetslipGroupPick } from '../../../core/utils';
import { OverAskFlowState, OverAskRejectReason } from '../../../model/over-ask/over-ask';
import { BetslipOverAskActions } from '../actions';
import {
    Overask_Betypes,
    TrackingAcceptOfferLabelType,
    TrackingOfferType,
    TrackingPendingRejectDialogActionType,
    TrackingReviewRejectDialogActionType,
} from '../models/over-ask-tracking.model';
import { overAskStateSelector } from '../selectors';
import { OverAskTrackingService } from '../services/over-ask-tracking.service';
import { IBetslipOverAskState } from '../state';
import { OverAskTrackingActions } from '../tracking.actions';

@Injectable()
export class OverAskTrackingEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private overAskTrackingService: OverAskTrackingService,
    ) {}

    handlePending$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipOverAskActions.startFlow),
            map(() => OverAskTrackingActions.trackPending()),
        );
    });

    trackPending$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(OverAskTrackingActions.trackPending),
                concatLatestFrom(() => this.store.select(overAskStateSelector)),
                concatLatestFrom(() => this.store.select(selectIsLinearBetslip)),
                tap(([[_, state], isLinear]) => {
                    const picks = this.getPicks(state);
                    this.overAskTrackingService.trackPending(picks, state.bets[0].type, isLinear, this.getBetType(state));
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackClose$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(OverAskTrackingActions.trackClose),
                concatLatestFrom(() => this.store.select(overAskStateSelector)),
                filter(([, state]) => state.flowState === OverAskFlowState.Pending || state.flowState === OverAskFlowState.Review),
                concatLatestFrom(() => this.store.select(selectIsLinearBetslip)),
                tap(([[action, state], isLinear]) => {
                    const betType = this.overAskTrackingService.getSlipType(state.bets[0].type, state.bets[0].picks.length > 1);

                    return state.flowState === OverAskFlowState.Pending
                        ? this.overAskTrackingService.trackPendingClose(action.method, betType, isLinear, this.getBetType(state))
                        : this.overAskTrackingService.trackReviewClose(action.method, isLinear);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackRejectDialogLoad$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipOverAskActions.userRequestRejectOffer),
                concatLatestFrom(() => this.store.select(overAskStateSelector)),
                filter(([, state]) => state.flowState === OverAskFlowState.Pending || state.flowState === OverAskFlowState.Review),
                concatLatestFrom(() => this.store.select(selectIsLinearBetslip)),
                tap(([[, state], isLinear]) => {
                    const betType = this.overAskTrackingService.getSlipType(state.bets[0].type, state.bets[0].picks.length > 1);

                    return state.flowState === OverAskFlowState.Pending
                        ? this.overAskTrackingService.trackPendingRejectDialogLoad(betType, isLinear, this.getBetType(state))
                        : this.overAskTrackingService.trackReviewRejectDialogLoad();
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackRejectDialogDismissAction$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(OverAskTrackingActions.trackRejectDialogActionDismiss),
                concatLatestFrom(() => this.store.select(overAskStateSelector)),
                filter(([, state]) => state.flowState === OverAskFlowState.Pending || state.flowState === OverAskFlowState.Review),
                concatLatestFrom(() => this.store.select(selectIsLinearBetslip)),
                tap(([[, state], isLinear]) => {
                    const betType = this.overAskTrackingService.getSlipType(state.bets[0].type, state.bets[0].picks.length > 1);

                    return state.flowState === OverAskFlowState.Pending
                        ? this.overAskTrackingService.trackPendingRejectDialogAction(
                              TrackingPendingRejectDialogActionType.GetOffer,
                              betType,
                              isLinear,
                              this.getBetType(state),
                          )
                        : this.overAskTrackingService.trackReviewRejectDialogAction(
                              TrackingReviewRejectDialogActionType.StayInOffer,
                              betType,
                              isLinear,
                              this.getBetType(state),
                          );
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackRejectDialogCancelAction$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(OverAskTrackingActions.trackRejectDialogActionCancel),
                concatLatestFrom(() => this.store.select(overAskStateSelector)),
                filter(([, state]) => state.flowState === OverAskFlowState.Pending || state.flowState === OverAskFlowState.Review),
                concatLatestFrom(() => this.store.select(selectIsLinearBetslip)),
                tap(([[, state], isLinear]) => {
                    const betType = this.overAskTrackingService.getSlipType(state.bets[0].type, state.bets[0].picks.length > 1);

                    return state.flowState === OverAskFlowState.Pending
                        ? this.overAskTrackingService.trackPendingRejectDialogAction(
                              TrackingPendingRejectDialogActionType.CancelOffer,
                              betType,
                              isLinear,
                              this.getBetType(state),
                          )
                        : this.overAskTrackingService.trackReviewRejectDialogAction(
                              TrackingReviewRejectDialogActionType.CancelOffer,
                              betType,
                              isLinear,
                              this.getBetType(state),
                          );
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleOverAskOddsChangeTracking$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(OverAskTrackingActions.trackOddsChange),
                tap((action) => {
                    if (action.overAskResponse?.status === PlaceBetStatus.Failed) {
                        const slipErrors = flatten(action.overAskResponse.betslips.map((slip) => slip.failed?.errors));
                        if (slipErrors.find((error) => [PlacementErrorType.OddsChanged].includes(error?.type ?? PlacementErrorType.OddsChanged))) {
                            this.overAskTrackingService.trackOddsChangeErrorOnOverAskBet();
                        }
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackTraderOffer$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(OverAskTrackingActions.trackTraderOffer),
                concatLatestFrom(() => this.store.select(overAskStateSelector)),
                concatLatestFrom(() => this.store.select(selectIsLinearBetslip)),
                tap(([[, state], isLinear]) => {
                    const changes = this.getOfferType(state);
                    const picks = this.getPicks(state);
                    this.overAskTrackingService.trackTraderOffer(picks, changes, isLinear);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackRejectError$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipOverAskActions.rejectOffer),
                concatLatestFrom(() => this.store.select(overAskStateSelector)),
                concatLatestFrom(() => this.store.select(selectIsLinearBetslip)),
                tap(([[action, state], isLinear]) => {
                    const picks = this.getPicks(state);
                    const betType = this.getBetType(state);

                    if (action.reason === OverAskRejectReason.BettingOfferNotAvailable) {
                        this.overAskTrackingService.trackRejectError(picks, 'offer expired', state.bets[0].type, isLinear, betType);

                        return;
                    }

                    if (action.reason === OverAskRejectReason.TraderRejectOffer) {
                        this.overAskTrackingService.trackRejectError(picks, 'trader denied', state.bets[0].type, isLinear, betType);

                        return;
                    }

                    if (action.reason === OverAskRejectReason.PlaceBetFailed) {
                        this.overAskTrackingService.trackRejectError(picks, 'bet placement failed', state.bets[0].type, isLinear, betType);

                        return;
                    }

                    if (action.reason === OverAskRejectReason.ArcUserProfileRestriction) {
                        this.overAskTrackingService.trackRejectError(picks, 'ARC User Profile Restriction', state.bets[0].type, isLinear, betType);

                        return;
                    }

                    if (action.reason === OverAskRejectReason.AcceptOfferTimeout) {
                        const offerType = this.getOfferType(state);

                        this.overAskTrackingService.trackTraderOfferExpired(picks, offerType, isLinear);
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackUserAcceptOffer$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipOverAskActions.acceptOffer),
                concatLatestFrom(() => this.store.select(overAskStateSelector)),
                concatLatestFrom(() => this.store.select(selectIsLinearBetslip)),
                tap(([[, state], isLinear]) => {
                    const picks = this.getPicks(state);
                    const type = this.overAskTrackingService.getSlipType(state.bets[0].type, state.bets[0].picks.length > 1);
                    this.overAskTrackingService.trackUserAcceptOffer(
                        picks,
                        TrackingAcceptOfferLabelType.AcceptOfferAndPlaceBet,
                        isLinear,
                        this.getBetType(state),
                        type,
                    );
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackAcceptBettingOfferChanges$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipOverAskActions.acceptBettingOfferChanges),
                concatLatestFrom(() => this.store.select(overAskStateSelector)),
                concatLatestFrom(() => this.store.select(selectIsLinearBetslip)),
                tap(([[, state], isLinear]) => {
                    const picks = this.getPicks(state);
                    const type = this.overAskTrackingService.getSlipType(state.bets[0].type, state.bets[0].picks.length > 1);
                    this.overAskTrackingService.trackUserAcceptOffer(
                        picks,
                        TrackingAcceptOfferLabelType.AcceptOfferChangesAndPlaceBet,
                        isLinear,
                        this.getBetType(state),
                        type,
                    );
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    private getPicks(state: IBetslipOverAskState): BetslipPick[] {
        return state.bets.flatMap((bet) => bet.picks.map((pick) => pick.pick));
    }

    private getOfferType(state: IBetslipOverAskState): TrackingOfferType {
        let hasOddsChange = false;
        let hasStakeChange = false;
        const picks: BetslipPick[] = [];

        state.bets.forEach((bet) => {
            picks.push(...bet.picks.map((pick) => pick.pick));

            if (bet.changes.stake && !hasStakeChange) {
                hasStakeChange = true;
            }

            hasOddsChange =
                hasOddsChange || bet.picks.some((pick) => !!pick.changes.price && !OddsOperations.isOddsEmpty(pick.changes.price.nativeOdds));
        });

        const changes: TrackingOfferType[] = [];

        if (hasStakeChange) {
            changes.push('new stake');
        }

        if (hasOddsChange) {
            changes.push('new odds');
        }

        return changes.join(' and ') as TrackingOfferType;
    }

    getBetType(state: IBetslipOverAskState): string {
        const picks = this.getPicks(state);

        const groupPicks = picks.filter(isBaseBetslipGroupPick);
        if (picks.length === 1 && groupPicks.length === 1) {
            return Overask_Betypes.sgp;
        } else if (groupPicks.length > 0) {
            return Overask_Betypes.sgpPlus;
        }

        return '';
    }
}
